/**
 * postMessage contract between the embedded Sam app (child) and its host
 * portal (parent).
 *
 * Envelope: `{ source, type, version: 1, payload }`.
 * - Child (this app) sends `source: "tessa-embed"`.
 * - Parent (portal) sends `source: "tessa-portal"`.
 *
 * The two `source` values keep the app's former name (TESSA) on purpose: they
 * are wire values the host portal already matches on, and renaming them here
 * would silently drop every message until the portal ships the same rename.
 * Never user-visible. See frontend/docs/portal-embed.md.
 *
 * Type guards are hand-rolled (no zod / no new deps) and pure.
 */

export const EMBED_SOURCE = "tessa-embed" as const;
export const PORTAL_SOURCE = "tessa-portal" as const;
export const PROTOCOL_VERSION = 1 as const;

/** Outbound: child -> parent, sent once on mount. */
export interface ReadyMessage {
  source: typeof EMBED_SOURCE;
  type: "ready";
  version: typeof PROTOCOL_VERSION;
  payload: Record<string, never>;
}

/** Outbound: child -> parent, content height changed. */
export interface ResizeMessage {
  source: typeof EMBED_SOURCE;
  type: "resize";
  version: typeof PROTOCOL_VERSION;
  payload: { height: number };
}

/** Inbound: parent -> child, set the accent colour. */
export interface ThemeMessage {
  source: typeof PORTAL_SOURCE;
  type: "theme";
  version: typeof PROTOCOL_VERSION;
  payload: { accent: string };
}

export type OutboundMessage = ReadyMessage | ResizeMessage;
export type InboundMessage = ThemeMessage;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Validate that `data` is a well-formed inbound (parent -> child) message.
 * Checks source, version, type, and payload shape.
 */
export function isInboundMessage(data: unknown): data is InboundMessage {
  if (!isRecord(data)) return false;
  if (data.source !== PORTAL_SOURCE) return false;
  if (data.version !== PROTOCOL_VERSION) return false;

  switch (data.type) {
    case "theme": {
      const payload = data.payload;
      return isRecord(payload) && typeof payload.accent === "string";
    }
    default:
      return false;
  }
}

export function buildReady(): ReadyMessage {
  return {
    source: EMBED_SOURCE,
    type: "ready",
    version: PROTOCOL_VERSION,
    payload: {},
  };
}

export function buildResize(height: number): ResizeMessage {
  return {
    source: EMBED_SOURCE,
    type: "resize",
    version: PROTOCOL_VERSION,
    payload: { height },
  };
}
